import { Button } from '@/shared/components/ui/Button';
import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { useRefetchOnOpen } from '@/shared/hooks/useRefetchOnOpen';
import { cn } from '@/shared/lib/cn';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, FileSpreadsheet, Loader2, Upload, X } from 'lucide-react';
import { useState } from 'react';
import { read, utils } from 'xlsx';
import { type BulkImportRow, bulkImportContacts } from './api';
import { useAccessGroups, useContactTags } from './hooks';

interface Props {
  onClose: () => void;
}

type ChatplusField = 'display_name' | 'phone' | 'email' | 'ignore';

interface ParsedSheet {
  headers: string[];
  rows: Record<string, unknown>[];
}

const FIELD_LABEL: Record<ChatplusField, string> = {
  display_name: 'Nome',
  phone: 'Telefone (E.164)',
  email: 'Email',
  ignore: 'Ignorar coluna',
};

const CHUNK_SIZE = 500;

export function ImportModal({ onClose }: Props) {
  useEscapeClose(true, onClose);
  const qc = useQueryClient();
  const [parsed, setParsed] = useState<ParsedSheet | null>(null);
  const [mapping, setMapping] = useState<Record<string, ChatplusField>>({});
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [result, setResult] = useState<{
    succeeded: number;
    failed: Array<{ row: number; error: string }>;
  } | null>(null);
  const tagsQ = useContactTags();
  useRefetchOnOpen(true, tagsQ.refetch);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const groupsQ = useAccessGroups();
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
  /** Arquivo pairando sobre a área — só pinta a borda. */
  const [arrastando, setArrastando] = useState(false);

  const handleFile = async (file: File): Promise<void> => {
    setError(null);
    // Só CSV — leve de parsear no browser; XLSX (zip+xml) pode travar a aba em
    // listas grandes. O `accept` filtra no seletor; este guard cobre drag-drop.
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Use um arquivo .csv (exporte do Excel como CSV).');
      return;
    }
    try {
      const buffer = await file.arrayBuffer();
      // CSV em UTF-8 → decodifica os bytes como UTF-8 e passa como string. Sem isso,
      // o SheetJS lê CSV sem BOM como Windows-1252 e quebra emojis/acentos (o emoji
      // vira "ð..."). O TextDecoder (ignoreBOM=false por padrão) já remove o BOM.
      const text = new TextDecoder('utf-8').decode(buffer);
      const wb = read(text, { type: 'string' });
      const sheetName = wb.SheetNames[0];
      if (!sheetName) throw new Error('Arquivo sem planilhas');
      const sheet = wb.Sheets[sheetName];
      if (!sheet) throw new Error('Planilha vazia');
      const json = utils.sheet_to_json(sheet, { defval: '' }) as Record<string, unknown>[];
      if (json.length === 0) throw new Error('Planilha sem linhas');
      const headers = Object.keys(json[0] ?? {});
      setParsed({ headers, rows: json });

      // Auto-mapping heurístico por nome de coluna.
      const auto: Record<string, ChatplusField> = {};
      for (const h of headers) {
        const lower = h.toLowerCase();
        if (lower.includes('nome') || lower === 'name') auto[h] = 'display_name';
        // "Numero", "Celular", "Fone", "Tel" — como as planilhas de cliente
        // costumam chamar a coluna. Antes só "telefone/phone/whats" casava e a
        // coluna vinha como "Ignorar", travando o botão de importar.
        else if (
          lower.includes('phone') ||
          lower.includes('whats') ||
          lower.includes('telefone') ||
          lower.includes('numero') ||
          lower.includes('número') ||
          lower.includes('celular') ||
          lower.includes('fone') ||
          lower === 'tel'
        )
          auto[h] = 'phone';
        else if (lower.includes('email') || lower.includes('e-mail')) auto[h] = 'email';
        else auto[h] = 'ignore';
      }
      setMapping(auto);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao ler arquivo');
    }
  };

  const mut = useMutation({
    mutationFn: async () => {
      if (!parsed) throw new Error('no file');

      const hasName = Object.values(mapping).includes('display_name');
      const hasPhone = Object.values(mapping).includes('phone');
      if (!hasName || !hasPhone) {
        throw new Error('Mapeie pelo menos as colunas de Nome e Telefone.');
      }

      const transformed: BulkImportRow[] = [];
      /**
       * Linha da PLANILHA de cada item enviado (1 = cabeçalho), pra o relatório
       * dizer "linha 7754" e a pessoa achar direto no arquivo. Sem isso o
       * número era a posição na lista já filtrada e apontava pro lugar errado.
       */
      const linhaDoArquivo: number[] = [];
      /** O que nem chega a ser enviado: sem nome ou sem telefone na planilha. */
      const ignoradasAqui: Array<{ row: number; error: string }> = [];
      let numeroDaLinha = 1;
      for (const row of parsed.rows) {
        numeroDaLinha += 1;
        const out: Partial<BulkImportRow> & { custom_fields: Record<string, unknown> } = {
          custom_fields: {},
        };
        for (const [src, target] of Object.entries(mapping)) {
          const v = row[src];
          if (target === 'ignore' || v === null || v === undefined || v === '') continue;
          if (target === 'display_name') out.display_name = String(v).trim();
          else if (target === 'phone') {
            const cleaned = String(v).replace(/\D/g, '');
            out.phone = cleaned.startsWith('55') ? `+${cleaned}` : `+55${cleaned}`;
          } else if (target === 'email') out.email = String(v).trim();
        }
        if (out.display_name && out.phone) {
          transformed.push(out as BulkImportRow);
          linhaDoArquivo.push(numeroDaLinha);
        } else {
          ignoradasAqui.push({
            row: numeroDaLinha,
            error: out.phone ? 'nome: obrigatório' : 'telefone: obrigatório',
          });
        }
      }

      const total = transformed.length;
      setProgress({ done: 0, total });

      let succeeded = 0;
      const failedAll: Array<{ row: number; error: string }> = [...ignoradasAqui];

      for (let i = 0; i < transformed.length; i += CHUNK_SIZE) {
        const chunk = transformed.slice(i, i + CHUNK_SIZE);
        const r = await bulkImportContacts({
          rows: chunk,
          tag_ids: selectedTagIds,
          access_group_ids: selectedGroupIds,
        });
        succeeded += r.succeeded;
        // `f.row` é a posição DENTRO do lote; vira a linha da planilha.
        for (const f of r.failed) {
          failedAll.push({ row: linhaDoArquivo[i + f.row] ?? i + f.row, error: f.error });
        }
        setProgress({ done: Math.min(i + CHUNK_SIZE, total), total });
      }

      return { succeeded, failed: failedAll.sort((a, b) => a.row - b.row) };
    },
    onSuccess: (r) => {
      setResult(r);
      void qc.invalidateQueries({ queryKey: ['contacts'] });
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Erro inesperado'),
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-[640px] max-w-full rounded-lg bg-n-0 p-6 shadow-lg">
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-[16px] text-n-900">Importar contatos</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-md text-n-500 hover:bg-n-100"
            aria-label="Fechar"
          >
            <X size={16} />
          </button>
        </div>

        {!parsed && (
          /**
           * Arrastar e soltar de verdade. A área dizia "Arraste ou clique" mas
           * só tinha o clique: sem `onDragOver`+`preventDefault`, o navegador
           * assume o arquivo e abre/baixa ele, levando a pessoa pra fora do
           * painel (relato do dono, 30/set).
           */
          <label
            onDragOver={(e) => {
              e.preventDefault();
              setArrastando(true);
            }}
            onDragLeave={() => setArrastando(false)}
            onDrop={(e) => {
              e.preventDefault();
              setArrastando(false);
              const file = e.dataTransfer.files?.[0];
              if (file) void handleFile(file);
            }}
            className={cn(
              'mt-5 flex cursor-pointer flex-col items-center gap-2 rounded-md border-2 border-dashed bg-n-50 p-12 text-center hover:border-brand-300',
              arrastando ? 'border-brand-500 bg-brand-50' : 'border-n-300',
            )}
          >
            <Upload size={32} className="text-n-400" />
            <span className="font-semibold text-[14px] text-n-700">Selecione um arquivo CSV</span>
            <span className="text-[12px] text-n-500">
              Arraste ou clique. Linha 1 deve conter cabeçalhos.
            </span>
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleFile(file);
              }}
            />
          </label>
        )}

        {parsed && !result && (
          <div className="mt-5">
            <div className="mb-3 flex items-center gap-2 text-[12px] text-n-700">
              <FileSpreadsheet size={14} className="text-brand-500" />
              {parsed.rows.length} linhas detectadas. Mapeie as colunas:
            </div>
            <div className="max-h-[300px] overflow-y-auto rounded-md border border-n-200">
              <table className="w-full text-[12px]">
                <thead className="border-b border-n-200 bg-n-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-n-700">Coluna XLS</th>
                    <th className="px-3 py-2 text-left font-semibold text-n-700">Campo ChatPlus</th>
                  </tr>
                </thead>
                <tbody>
                  {parsed.headers.map((h) => (
                    <tr key={h} className="border-b border-n-100 last:border-b-0">
                      <td className="px-3 py-2 font-medium text-n-900">{h}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <ChevronRight size={12} className="text-n-400" />
                          <select
                            value={mapping[h] ?? 'ignore'}
                            onChange={(e) =>
                              setMapping({ ...mapping, [h]: e.target.value as ChatplusField })
                            }
                            className="rounded-md border border-n-200 bg-n-0 px-2 py-1 text-[12px]"
                          >
                            {(Object.keys(FIELD_LABEL) as ChatplusField[]).map((f) => (
                              <option key={f} value={f}>
                                {FIELD_LABEL[f]}
                              </option>
                            ))}
                          </select>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {(tagsQ.data ?? []).length > 0 && (
              <div className="mt-4">
                <div className="mb-1.5 text-[12px] font-semibold text-n-700">
                  Vincular etiquetas <span className="font-normal text-n-400">(opcional)</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(tagsQ.data ?? []).map((t) => {
                    const active = selectedTagIds.includes(t.id);
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() =>
                          setSelectedTagIds((prev) =>
                            active ? prev.filter((id) => id !== t.id) : [...prev, t.id],
                          )
                        }
                        className={`rounded-pill border px-2.5 py-1 font-semibold text-[10px] transition ${
                          active
                            ? 'border-transparent text-white'
                            : 'border-n-200 bg-n-0 text-n-700 hover:border-brand-300'
                        }`}
                        style={active ? { background: t.color } : undefined}
                      >
                        {t.name}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-1 text-[11px] text-n-500">
                  As etiquetas marcadas serão vinculadas a todos os contatos deste arquivo.
                </p>
              </div>
            )}

            {(groupsQ.data ?? []).length > 0 && (
              <div className="mt-4">
                <div className="mb-1.5 text-[12px] font-semibold text-n-700">
                  Grupo de acesso <span className="font-normal text-n-400">(opcional)</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(groupsQ.data ?? []).map((g) => {
                    const active = selectedGroupIds.includes(g.id);
                    return (
                      <button
                        key={g.id}
                        type="button"
                        onClick={() =>
                          setSelectedGroupIds((prev) =>
                            active ? prev.filter((id) => id !== g.id) : [...prev, g.id],
                          )
                        }
                        className={`rounded-pill border px-2.5 py-1 font-semibold text-[10px] transition ${
                          active
                            ? 'border-transparent bg-brand-500 text-white'
                            : 'border-n-200 bg-n-0 text-n-700 hover:border-brand-300'
                        }`}
                      >
                        {g.name}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-1 text-[11px] text-n-500">
                  Os grupos marcados serão vinculados a todos os contatos deste arquivo (empresa).
                </p>
              </div>
            )}

            {progress && (
              <div className="mt-4 rounded-md bg-brand-50 px-3 py-2 text-[12px] text-brand-700">
                Importando {progress.done} de {progress.total}…
              </div>
            )}
            {error && (
              <div className="mt-4 rounded-md bg-danger-50 px-3 py-2 text-[12px] text-danger-700">
                {error}
              </div>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <Button variant="ghost" onClick={onClose} disabled={mut.isPending}>
                Cancelar
              </Button>
              <Button onClick={() => mut.mutate()} disabled={mut.isPending}>
                {mut.isPending && <Loader2 size={12} className="animate-spin" />}
                Importar {parsed.rows.length} contatos
              </Button>
            </div>
          </div>
        )}

        {result && (
          <div className="mt-5">
            <div className="rounded-md bg-success-50 px-4 py-3 text-[13px] text-success-700">
              ✓ {result.succeeded} contatos importados com sucesso.
            </div>
            {result.failed.length > 0 && (
              <div className="mt-3 max-h-[160px] overflow-y-auto rounded-md bg-warning-50 px-4 py-3 text-[12px] text-warning-700">
                <div className="font-bold">
                  {result.failed.length} linhas ignoradas (o resto foi importado):
                </div>
                <ul className="mt-1 list-disc pl-4">
                  {result.failed.slice(0, 10).map((f) => (
                    <li key={f.row}>
                      Linha {f.row}: {f.error}
                    </li>
                  ))}
                  {result.failed.length > 10 && <li>… e mais {result.failed.length - 10}</li>}
                </ul>
              </div>
            )}
            <div className="mt-5 flex justify-end">
              <Button onClick={onClose}>Fechar</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
